import { cva, type VariantProps } from 'class-variance-authority'
import { useId, type ComponentPropsWithRef, type ReactNode } from 'react'
import { cn } from '../../lib/cn'
import { Eyebrow } from '../Eyebrow/Eyebrow'
import s from './Field.module.css'

/**
 * Champ libellé — le `<label>` que le prototype pose au-dessus de ses contrôles, et le
 * frère saisissable de `DataField` annoncé plus bas.
 *
 * Pas de primitive Base UI : un `<label>` natif porte déjà le rattachement et le clic qui
 * donne le focus au contrôle (corollaire de la règle 3, comme `Button`).
 *
 * `children` est une **fonction**, et ce détour n'est pas de la coquetterie : un `<input>`
 * natif se rattache par `htmlFor`, alors que le déclencheur de `Select` est un `<button>`
 * — il ne se rattache que par `aria-labelledby`. Le libellé porte donc les **deux**
 * identifiants et laisse le contrôle prendre celui qui le concerne ; choisir à sa place
 * rendrait l'un des deux muet au lecteur d'écran.
 *
 * ⚠️ `hint` est du texte d'**aide** — format attendu, provenance, ce que le serveur va
 * faire — pas un message d'erreur : l'invalidité reste portée par `invalid` sur le
 * contrôle, qui la peint et l'annonce déjà.
 *
 * ⚠️ **`hintId` est dans le sac, et il n'est pas décoratif** : une aide qui dit ce que le
 * serveur va faire d'irréversible (« changer le rôle déconnecte la personne ») ne peut pas
 * n'exister qu'à l'œil. Il désigne l'aide **et** l'avertissement (une liste d'identifiants), et
 * vaut `undefined` sans l'un ni l'autre, ce qui rend `aria-describedby={hintId}` sûr à écrire sans
 * condition.
 */
export interface FieldProps {
  /**
   * Le libellé, **visible** — pas un `aria-label` : un champ se lit aussi à l'œil. Un nœud plutôt
   * qu'un texte pour une mention qui s'en distingue (« Référence GHP · clé unique », `importOcr`).
   */
  label: ReactNode
  /** Le contrôle, rendu avec les identifiants à poser dessus. */
  children: (ids: { id: string; labelId: string; hintId: string | undefined }) => ReactNode
  /** Une précision sous le contrôle : ce qui est dérivé, ce qui sera fait, une réserve. */
  hint?: ReactNode
  /** Ce que la saisie coûte, sans l'empêcher (M21) — ou `null`. */
  warning?: ReactNode
  className?: string
}

export function Field({ label, children, hint, warning, className }: FieldProps) {
  // `useId()` et non un compteur maison : c'est ce qui rend l'identifiant stable entre le
  // rendu serveur et le rendu client, et unique quand l'écran monte dix fois le même champ.
  const id = useId()
  const labelId = useId()
  const helpId = useId()
  const warningId = useId()
  const warned = warning !== undefined && warning !== null
  const described = [hint !== undefined && helpId, warned && warningId].filter(Boolean).join(' ')
  const hintId = described === '' ? undefined : described

  return (
    <div className={cn(s.root, className)}>
      <label id={labelId} htmlFor={id} className={s.label}>
        {label}
      </label>
      {children({ id, labelId, hintId })}
      {hint !== undefined && (
        <span id={helpId} className={s.hint}>
          {hint}
        </span>
      )}
      {warned && (
        <span id={warningId} className={s.warning}>
          {warning}
        </span>
      )}
    </div>
  )
}

/**
 * Couple terme / valeur en lecture — le `.field` du prototype quand il n'entoure pas un
 * contrôle : une étiquette grise au-dessus d'une valeur encadrée.
 *
 * ⚠️ Volontairement **pas** de `<label>`. Une étiquette sans contrôle n'étiquette rien, et
 * un lecteur d'écran l'annoncerait comme un texte flottant : c'est un `<div>` et un
 * `<span>`. Un contrôle saisissable prend son frère `Field` ci-dessus, qui rend un vrai
 * `<label>` rattaché — jamais une prop de plus sur celui-ci.
 *
 * L'écartement vertical appartient au conteneur (le `gap` de la grille de l'écran), pas au
 * champ — voir l'entête du module CSS.
 */
export interface DataFieldProps {
  label: ReactNode
  /** Précision sous la valeur — format attendu, provenance de la donnée… */
  hint?: ReactNode
  /**
   * Le champ des **fiches** du back-office (`info()` du prototype). Une variante : le défaut, sans
   * capitales, sert aussi la tablette.
   */
  eyebrow?: boolean
  children?: ReactNode
  className?: string
}

export function DataField({ label, hint, eyebrow, children, className }: DataFieldProps) {
  return (
    <div className={cn(s.root, eyebrow === true && s.eyebrow, className)}>
      {eyebrow === true ? (
        <Eyebrow className={s.eyebrowLabel}>{label}</Eyebrow>
      ) : (
        <span className={s.label}>{label}</span>
      )}
      {children}
      {hint !== undefined && <span className={s.hint}>{hint}</span>}
    </div>
  )
}

/**
 * La valeur d'une **fiche** : du texte nu, 14 px semi-gras — le `info()` du prototype.
 *
 * ⚠️ **À ne pas confondre avec `FieldValue`**, qui dessine le cadre d'un `Input` sans saisie. Ce
 * cadre a sa place là où une fiche alterne lecture et écriture ; sur une fiche **entièrement** en
 * lecture, il donne à l'écran l'air d'un formulaire désactivé — défaut relevé sur la première
 * version de la fiche membre, où les trois champs ressemblaient à des champs grisés.
 */
export function DataValue({ className, ...props }: ComponentPropsWithRef<'div'>) {
  return <div className={cn(s.dataValue, className)} {...props} />
}

/**
 * La boîte de valeur — le `.val` du prototype : même cadre qu'un `Input`, sans saisie.
 * C'est ce qui fait qu'une fiche garde une seule grille visuelle au lieu d'alterner des
 * boîtes et du texte nu.
 *
 * `readOnly` ne veut pas dire « désactivé » : la valeur existe et se lit, elle est
 * simplement gérée ailleurs — le back-office, pour ce que le terrain ne propose pas. Le
 * fond gris du prototype le dit sans avoir à l'écrire.
 */
const fieldValue = cva(s.value, {
  variants: {
    /** Références, mesures, coordonnées : la famille mono du prototype. */
    mono: { true: s.valueMono },
    /** Valeur absente — allège la graisse plutôt que d'afficher un vide muet. */
    empty: { true: s.valueEmpty },
    readOnly: { true: s.valueReadOnly },
    /** Texte libre : la boîte s'étire au lieu de tronquer. */
    multiline: { true: s.valueMultiline },
  },
})

export type FieldValueProps = ComponentPropsWithRef<'div'> & VariantProps<typeof fieldValue>

export function FieldValue({
  mono,
  empty,
  readOnly,
  multiline,
  className,
  ...props
}: FieldValueProps) {
  return <div className={fieldValue({ mono, empty, readOnly, multiline, className })} {...props} />
}
